/**
 * Shared in-flight rebuild lock for the live-price rebuild paths.
 *
 * Originally written twice, identically, inside ProductLiveBuild and
 * CategoryLiveBuild (2026-08-24 double-fire fix) and missing entirely from
 * StaleCeilingRefresh. Extracted here on 2026-09-12 so all three share ONE
 * lock: the three paths rebuild the same pages, so a lock one path takes must
 * be visible to the others or it does not actually stop a duplicate.
 *
 * Why a lock at all: a rebuild is a full paid search. Without it, every visit
 * and every reload inside the rebuild window starts ANOTHER one for the same
 * page (original evidence: two identical "Apple iPhone 17 Pro" searches at
 * 20:52 and 20:54, the second fired while the first was still running).
 *
 * localStorage, so it is shared across this browser's tabs and survives a
 * reload, which is the exact gesture that used to fire duplicates. It does NOT
 * reach another device; that would need a server-side in-flight flag and a
 * migration, and is not built.
 *
 * TTL, measured 2026-09-12 on production rather than guessed:
 *   foreground search, 563 completed searches over 14 days
 *       median 78s, p90 118s, p95 136s, p99 205s, max 268s
 *   background pSEO refresh (what actually turns the page fresh), n=54
 *       median 93s, p90 146s, max 189s
 * The previous 3 minute (180s) TTL sat BELOW both maxima: 14 of 563 searches
 * (2%) and 2 of 54 refreshes (4%) outlived it, so the lock expired while the
 * cycle was still running and the next visit paid for a duplicate. 6 minutes
 * covers the slowest observed case (268s) with 34% headroom.
 *
 * A longer TTL cannot strand a page: the lock only suppresses the expensive
 * FOREGROUND rebuild. The background refresh (B-201) still runs and still
 * binds, so the page becomes fresh regardless, and once it is fresh none of
 * these paths are eligible anyway.
 */
export const BUILD_LOCK_TTL_MS = 6 * 60 * 1000;

/**
 * Try to take the rebuild lock for `key`. Returns true when the caller owns
 * it and should start the paid rebuild, false when a rebuild for the same key
 * is already in flight in this browser.
 *
 * Storage unavailable (private mode, blocked site data): returns true. Firing
 * without the lock is better than a page that can never refresh.
 */
export function acquireBuildLock(key: string): boolean {
  try {
    const k = `lp_build:${key}`;
    const now = Date.now();
    const held = Number(window.localStorage.getItem(k) || 0);
    if (held && now - held < BUILD_LOCK_TTL_MS) return false;
    window.localStorage.setItem(k, String(now));
    return true;
  } catch {
    return true;
  }
}
